/* GASSISA
=========================================================================================
* Descripcion: Registro local seguro de fallos del frontend
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 31/08/2026 | Lic. Jose Luis Aragon Cervantes  | Bitacora local de errores para monitoreo
*      2       | 31/08/2026 | Lic. Jose Luis Aragon Cervantes  | Limpieza automatica diaria del log local
* ---------------------------------------------------------------------------------------
*/

export const errorLogStorageKey = "sizesoftx-error-monitor-log";
export const errorLogStorageLimit = 300;
export const errorLogStorageDateKey = "sizesoftx-error-monitor-log-date";

const sensitiveKeys = [
  "authorization",
  "bearer",
  "token",
  "refresh",
  "password",
  "contrasena",
  "contrasena",
  "clavesecreta",
  "clave_secreta",
  "secret",
  "apikey",
  "api_key",
  "cookie",
  "set-cookie",
  "idphonewhatsapp",
  "tokenaccesowhatsapp",
];

const cleanText = (value, maxLength = 500) =>
  String(value ?? "")
    .replace(/[\r\n\t]+/g, " ")
    .replace(/\s{2,}/g, " ")
    .trim()
    .slice(0, maxLength);

const hasSensitiveKey = (value) => {
  const text = String(value ?? "").toLowerCase();
  return sensitiveKeys.some((key) => text.includes(key));
};

const getCurrentOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://sizesoftx.local";
};

const sanitizeUrl = (rawUrl) => {
  const value = cleanText(rawUrl, 900);
  if (!value) return "";

  try {
    const url = new URL(value, getCurrentOrigin());
    const params = [];
    url.searchParams.forEach((paramValue, key) => {
      params.push(`${key}=${hasSensitiveKey(key) || hasSensitiveKey(paramValue) ? "[OCULTO]" : "[VALOR]"}`);
    });

    return `${url.origin}${url.pathname}${params.length ? `?${params.join("&")}` : ""}`;
  } catch {
    return value.split("?")[0];
  }
};

const getModuleFromUrl = (url) => {
  const safeUrl = sanitizeUrl(url).toLowerCase();
  if (safeUrl.includes("/ventas/")) return "Ventas";
  if (safeUrl.includes("/catalogoaccesos/")) return "Accesos";
  if (safeUrl.includes("/catalogo")) return "Catalogos";
  if (safeUrl.includes("/movimientosinventarios/") || safeUrl.includes("/inventarios/")) return "Inventarios";
  if (safeUrl.includes("/seguridad/")) return "Seguridad";
  if (safeUrl.includes("/whatsapp/")) return "WhatsApp";
  if (safeUrl.includes("/subprocess/")) return "SubProcess";
  return "Sistema";
};

const todayLocal = () => {
  try {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

const enforceDailyClean = () => {
  if (typeof window === "undefined") return;

  try {
    const today = todayLocal();
    if (!today) return;

    const storedDate = window.localStorage.getItem(errorLogStorageDateKey);
    if (storedDate === today) return;

    window.localStorage.removeItem(errorLogStorageKey);
    window.localStorage.setItem(errorLogStorageDateKey, today);
  } catch {
    // No se propaga para no afectar el flujo principal del sistema.
  }
};

const readRawLogs = () => {
  if (typeof window === "undefined") return [];

  enforceDailyClean();

  try {
    const raw = window.localStorage.getItem(errorLogStorageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeRawLogs = (logs) => {
  if (typeof window === "undefined") return;

  enforceDailyClean();

  try {
    const limited = (Array.isArray(logs) ? logs : []).slice(0, errorLogStorageLimit);
    window.localStorage.setItem(errorLogStorageKey, JSON.stringify(limited));
    window.localStorage.setItem(errorLogStorageDateKey, todayLocal());
  } catch {
    // No se propaga para no afectar el flujo principal del sistema.
  }
};

export const getErrorLogs = () => readRawLogs();

export const removeErrorLog = (id) => {
  const nextLogs = readRawLogs().filter((item) => item.id !== id);
  writeRawLogs(nextLogs);
  return nextLogs;
};

export const clearErrorLogs = () => {
  writeRawLogs([]);
  return [];
};

export const registerRequestErrorLog = ({
  httpCode = 0,
  method = "FETCH",
  url = "",
  message = "",
  source = "RequestContext",
  severity = "error",
} = {}) => {
  const safeUrl = sanitizeUrl(url);
  const entry = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    fecha: new Date().toISOString(),
    modulo: getModuleFromUrl(safeUrl),
    origen: cleanText(source, 120),
    metodo: cleanText(method, 12).toUpperCase(),
    httpCode: Number(httpCode) || 0,
    severidad: cleanText(severity, 20),
    ruta: safeUrl,
    pantalla: typeof window !== "undefined" ? cleanText(window.location.pathname, 200) : "",
    mensaje: cleanText(message || "Fallo al consumir recurso", 700),
    pista: `Revisar respuesta ${Number(httpCode) || "sin codigo"} en ${getModuleFromUrl(safeUrl)}. No se almacenaron credenciales ni payloads.`,
  };

  const nextLogs = [entry, ...readRawLogs()].slice(0, errorLogStorageLimit);
  writeRawLogs(nextLogs);
  return entry;
};

export const buildErrorLogWhatsAppMessage = (logs = []) => {
  const selected = (Array.isArray(logs) ? logs : []).slice(0, 20);
  const sep = "-".repeat(32);
  const lines = [
    "*SIZESOFTX - MONITOREO DE FALLOS*",
    sep,
    `Registros: ${selected.length}`,
  ];

  selected.forEach((log, index) => {
    lines.push(
      sep,
      `#${index + 1} ${log.fecha || ""}`,
      `Modulo: ${log.modulo || "Sistema"}`,
      `Origen: ${log.origen || ""}`,
      `HTTP: ${log.httpCode || 0}`,
      `Metodo: ${log.metodo || ""}`,
      `Pantalla: ${log.pantalla || ""}`,
      `Ruta: ${log.ruta || ""}`,
      `Mensaje: ${log.mensaje || ""}`,
      `Pista: ${log.pista || ""}`
    );
  });

  lines.push(sep, "No incluye tokens, contrasenas, claves secretas ni payloads.");
  return lines.join("\n");
};
